import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import Iconify from "minimals-template/components/Iconify"
import { linkButtonSx, linkedButtonSx, unlinkButtonPositionSx } from "./component-link-button-styles"
import { preventPropagation } from "lib/prevent"
import { Frequency } from "routes/schedule/tasks/frequency"
import { HighlightBox } from "lib/@components/highlightBox"
import { FrequencyLabel, parseFrequencyCode } from "./component-ref-frequency-label"

const FIELD = "frequency"

export function FrequencyControl() {
    const { componentRef, linkedFields, onEnableField, onDisableField, editMode, componentTask } = useBoundContext()
    const isLinked = !!componentRef && linkedFields?.[FIELD]
    const isLoading = componentRef && componentTask === IS_LOADING

    if (!isLinked) {
        const canLink = !!componentRef && componentHasFrequency(componentTask)

        return (
            <>
                <Frequency />
                {editMode && canLink && (
                    <Box sx={unlinkButtonPositionSx}>
                        <Tooltip title="Link frequency from component">
                            <IconButton
                                size="small"
                                onClick={preventPropagation(handleEnable)}
                                className="component-link-btn"
                                sx={linkButtonSx}
                                aria-label="Link frequency"
                            >
                                <Iconify icon="mdi:puzzle-plus-outline" sx={{ width: 14, height: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </>
        )
    }

    if (isLoading) {
        return (
            <Box display="inline-flex" alignItems="center" sx={{ width: 24, height: 24 }}>
                <CircularProgress size={16} />
            </Box>
        )
    }

    if (!componentTask) return <Frequency />

    const freq = componentTask.frequency || parseFrequencyCode(componentTask.frequencyCode)

    return (
        <HighlightBox path="frequency" display="inline-flex" alignItems="center">
            <Tooltip title={`Inherited from: ${componentTask.heading ?? componentRef}`}>
                <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ opacity: editMode ? 0.6 : 1 }}>
                    <FrequencyLabel frequency={freq} />
                    {editMode && (
                        <IconButton
                            size="small"
                            onClick={preventPropagation(handleDisable)}
                            sx={linkedButtonSx}
                            aria-label="Unlink frequency"
                        >
                            <Iconify icon="mdi:link-variant" sx={{ width: 14, height: 14 }} />
                        </IconButton>
                    )}
                </Box>
            </Tooltip>
        </HighlightBox>
    )

    function handleEnable() {
        onEnableField?.(FIELD)
    }

    function handleDisable() {
        onDisableField?.(FIELD)
    }
}

function componentHasFrequency(task) {
    if (!task || task === IS_LOADING) return false
    return !!(task.frequency || task.frequencyCode)
}
